import classNames from "classnames";
import {
  CompanyParticipationType,
  ErrorMessages,
  ESubmittalStatus,
  EUserTypes,
  isGcProjectAdminRole,
  ProjectPermissionEnum,
  UserParticipationType
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { useContext, useMemo } from "react";
import { getLoggedinUserId } from "services/auth";
import { isProjectInIntegrationMode, matchUserProjectRole } from "utils/utils";

const findCompanyParticipant = (
  participants: any[] | undefined,
  typeId: CompanyParticipationType
) =>
  participants?.find(
    (company: any) => company?.company_participation_type_id === typeId
  );

const findUserParticipant = (
  participants: any[] | undefined,
  typeId: UserParticipationType
) =>
  participants?.find(
    (user: any) => user?.user_participation_type?.id === typeId
  );

const buildUserFromSourceInfo = (userSourceInfo: any) => {
  const fullName = `${userSourceInfo.first_name} ${userSourceInfo.last_name}`;
  return {
    id: fullName,
    first_name: fullName,
    vendor_source_info: userSourceInfo.vendor_source_info
  };
};

const resolveParticipantWithSourceInfo = (participant: any) => {
  if (participant?.user) return participant;
  if (participant?.user_source_info) {
    return { user: buildUserFromSourceInfo(participant.user_source_info) };
  }
  return null;
};

const resolveParticipantWithUnregistered = (participant: any) => {
  if (participant?.user) return participant;
  if (participant?.user_unregistered) {
    return {
      user: {
        id: participant.user_unregistered,
        first_name: participant.user_unregistered
      }
    };
  }
  if (participant?.user_source_info) {
    return { user: buildUserFromSourceInfo(participant.user_source_info) };
  }
  return null;
};

const getAssigneeCompany = (assignee: any) =>
  assignee?.user?.project_users?.length
    ? (assignee.user.project_users[0].subscription_vendor?.name ?? "")
    : "";

const buildAssigneeUnregisteredPoc = (
  assignee: any,
  drReviewer: any,
  submitter: any,
  drCompany: any,
  contractor: any
) => {
  if (!assignee?.user_unregistered) return null;

  if (assignee.user_unregistered === drReviewer?.user_unregistered) {
    return {
      id: `${drReviewer?.user_unregistered}`,
      fullName: `${drReviewer?.user_unregistered}`,
      company: {
        name: drCompany?.vendor_unregistered ?? drCompany?.vendor?.name ?? ""
      },
      type: "poc"
    };
  }

  return {
    id: `${submitter?.user_unregistered}`,
    fullName: `${submitter?.user_unregistered}`,
    company: {
      name: contractor?.vendor_unregistered ?? contractor?.vendor?.name ?? ""
    },
    type: "poc"
  };
};

const buildSubmittalParticipants = (featureInstanceData: any) => {
  const companyParticipants = featureInstanceData?.feature_company_participants;
  const userParticipants = featureInstanceData?.feature_user_paticipants;

  const contractor = findCompanyParticipant(
    companyParticipants,
    CompanyParticipationType.RESPONSIBLE_CONTRACTOR
  );
  const drCompany = findCompanyParticipant(
    companyParticipants,
    CompanyParticipationType.DESIGN_REVIEWER_VENDOR
  );
  const assignee = findUserParticipant(
    userParticipants,
    UserParticipationType.ASSIGNEE
  );
  const submitter = findUserParticipant(
    userParticipants,
    UserParticipationType.SUBMITTER
  );
  const gcReviewer = findUserParticipant(
    userParticipants,
    UserParticipationType.GC_REVIEWER
  );
  const drReviewer = findUserParticipant(
    userParticipants,
    UserParticipationType.DESIGN_REVIEWER
  );

  return {
    contractor,
    assignee: resolveParticipantWithSourceInfo(assignee),
    submitter: resolveParticipantWithUnregistered(submitter),
    gcReviewer: resolveParticipantWithSourceInfo(gcReviewer),
    drReviewer: resolveParticipantWithUnregistered(drReviewer),
    drCompany,
    assigneeCompany: getAssigneeCompany(assignee),
    assigneeUnregistered: () =>
      buildAssigneeUnregisteredPoc(
        assignee,
        drReviewer,
        submitter,
        drCompany,
        contractor
      )
  };
};

const hasMissingWorkflowParticipants = (participants: any) =>
  !participants?.gcReviewer?.user?.id ||
  !participants?.drReviewer?.user?.id ||
  !participants?.submitter?.user?.id;

const buildGcPendingUserSelectMessage = (participants: any) => {
  const pendingUserSelect: string[] = [];
  if (!participants?.submitter?.user?.id) {
    pendingUserSelect.push("Submitter");
  }
  if (!participants?.gcReviewer?.user?.id) {
    pendingUserSelect.push("GC Reviewer");
  }
  if (!participants?.drReviewer?.user?.id) {
    pendingUserSelect.push("Design Reviewer");
  }
  return `To continue, please select a ${pendingUserSelect.join(", ")}.`;
};

export const useSubmmittialDetails = (props: {
  featureInstanceData: any;
  isRevisionMode: boolean;
  submittal?: any;
  selectedRC?: any;
  isWorkflowInOverrideMode?: boolean;
  editableFields?: any;
  submittalViewState?: any;
}) => {
  const {
    featureInstanceData,
    isRevisionMode,
    submittal,
    selectedRC,
    isWorkflowInOverrideMode,
    editableFields,
    submittalViewState
  } = props;

  const { tokenContents, projectDetails, isProjectArchived } =
    useContext(ProjectContext);

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );
  const cannotCreateSubmittal = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateSubmittal,
    tokenContents?.role ?? ""
  );
  const cannotRespondToWorkflow = isPermissionNotGrantted(
    ProjectPermissionEnum.SubmittalWorkflow,
    tokenContents?.role ?? ""
  );
  const isSubmittalInDoneState =
    submittalViewState?.status === ESubmittalStatus.DONE;

  const isLoggedInUserGcAdmin = isGcProjectAdminRole(tokenContents?.role);

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isLoggedInUserSC = matchUserProjectRole(
    EUserTypes.SUBCONTRACTOR,
    tokenContents?.role
  );

  const isLoggedInUserArchitect = matchUserProjectRole(
    EUserTypes.ARCHITECT,
    tokenContents?.role
  );

  const isLoggedInUserEngineer = matchUserProjectRole(
    EUserTypes.ENGINEER,
    tokenContents?.role
  );

  const isLoggedInUserOwner = matchUserProjectRole(
    EUserTypes.OWNER,
    tokenContents?.role
  );

  const contentClassNames = classNames({
    "h-[330px]": !isLoggedInUserSC,
    "h-[250px]": isLoggedInUserSC
  });

  const submittalParticipants = useMemo(
    () => buildSubmittalParticipants(featureInstanceData),
    [featureInstanceData]
  );

  const loggedInUserId = useMemo(() => {
    return getLoggedinUserId();
  }, []);

  const isLoggedInUserWFGCUser =
    submittalParticipants?.gcReviewer?.user?.id === loggedInUserId;
  const isCurrentAssigneeGCUser =
    submittalParticipants?.assignee?.user?.id ===
    submittalParticipants?.gcReviewer?.user?.id;

  const isLoggedInUserMainInWorkflow =
    loggedInUserId === submittalParticipants?.gcReviewer?.user?.id ||
    loggedInUserId === submittalParticipants?.drReviewer?.user?.id ||
    loggedInUserId === submittalParticipants?.submitter?.user?.id;

  const showReviewOption =
    (isLoggedInUserMainInWorkflow || isLoggedInUserGC) &&
    !isLoggedInUserSC &&
    submittalViewState?.status === ESubmittalStatus.REVIEW;

  const checkOverrideForActualUsers =
    submittalParticipants?.assignee?.user?.id &&
    submittalParticipants?.assignee?.user?.id !== loggedInUserId;

  const checkOverrideForPOCUsers =
    submittal?.assignee_unregistered &&
    (submittal?.assignee_unregistered === submittal?.submitter_unregistered ||
      submittal?.assignee_unregistered ===
        submittal?.design_reviewer_unregistered);

  const showOverrideWorkflowBtn =
    isLoggedInUserGcAdmin &&
    !isProjectArchived &&
    (checkOverrideForActualUsers || checkOverrideForPOCUsers);

  const loggedInUser = featureInstanceData?.feature_user_paticipants?.find(
    (user: any) => user?.user?.id === loggedInUserId
  );

  const getGeneralInfoFieldsDisabledTooltipMsg = () => {
    if (isProjectArchived) return "";
    if (isLoggedInUserSC) return "";
    if (isRevisionMode) return ErrorMessages.RevisionMode;
    if (isGcProjectAdminRole(tokenContents?.role)) return "";
    if (isIntegrationMode)
      return ErrorMessages.FieldDisabledDueToIntegrationMode;
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    if (isSubmittalInDoneState)
      return "No actions can be performed as the submittal is already closed";
    if (submittalViewState?.status !== ESubmittalStatus.CREATE) {
      return "Details cannot be updated as workflow has started";
    }
    return "";
  };

  const generalInfoDisabledTooltipMsg =
    getGeneralInfoFieldsDisabledTooltipMsg();

  const getWorkflowFieldsDisabledTooltipMsg = () => {
    if (isProjectArchived) return "";
    if (isLoggedInUserSC || !submittalViewState) return "";
    if (isIntegrationMode)
      return ErrorMessages.FieldDisabledDueToIntegrationMode;
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    if (isSubmittalInDoneState)
      return "No actions can be performed as the submittal is already closed";
    if (
      submittalViewState &&
      submittalViewState?.status !== ESubmittalStatus.CREATE
    ) {
      return "Details cannot be updated as workflow has started";
    }
    return "";
  };

  const getSubmitterDisabledTooltipMsg = () => {
    if (isProjectArchived) return "";
    if (isLoggedInUserSC) return "";
    if (isIntegrationMode)
      return ErrorMessages.FieldDisabledDueToIntegrationMode;
    if (cannotCreateSubmittal) return ErrorMessages.PermissionNotGranted;
    if (isSubmittalInDoneState)
      return "No actions can be performed as the submittal is already closed";
    if (
      submittalViewState &&
      !cannotCreateSubmittal &&
      submittalViewState?.status !== ESubmittalStatus.CREATE
    ) {
      return "Details cannot be updated as workflow has started";
    }

    if (!cannotCreateSubmittal && !selectedRC) {
      return "First select a Responsible Contractor";
    }

    return "";
  };

  const getAttachBtnDisabledMsg = () => {
    if (isProjectArchived) return "";
    if (isLoggedInUserSC) return "";
    if (cannotRespondToWorkflow) {
      return ErrorMessages.PermissionNotGranted;
    }

    if (isWorkflowInOverrideMode) return "";

    if (!editableFields?.includes("attachment")) {
      return "Only the Ball In court user can currently add attachments";
    }

    return "";
  };

  const getAssignDisabledTooltipMsg = () => {
    if (isProjectArchived) return "";
    if (isWorkflowInOverrideMode || isRevisionMode) return "";

    if (cannotRespondToWorkflow) {
      return ErrorMessages.PermissionNotGranted;
    }

    if (submittalViewState?.status === ESubmittalStatus.DONE) {
      return "No actions can be performed as the submittal is already closed";
    }

    if (
      isLoggedInUserGC &&
      editableFields?.includes("comment") &&
      hasMissingWorkflowParticipants(submittalParticipants)
    ) {
      return buildGcPendingUserSelectMessage(submittalParticipants);
    }

    if (
      !editableFields?.includes("comment") &&
      loggedInUserId === submittalParticipants?.assignee?.user?.id &&
      submittalParticipants?.assignee?.user?.id !==
        submittalParticipants?.gcReviewer?.user?.id &&
      submittalParticipants?.assignee?.user?.id !==
        submittalParticipants?.drReviewer?.user?.id &&
      submittalParticipants?.assignee?.user?.id !==
        submittalParticipants?.submitter?.user?.id
    ) {
      return "";
    }

    if (!editableFields?.includes("comment")) {
      return "Only The Ball In Court user can currently perform an action";
    }

    return "";
  };
  const assignDisabledTooltipMsg = getAssignDisabledTooltipMsg();

  return {
    isIntegrationMode,
    cannotRespondToWorkflow,
    cannotCreateSubmittal,
    isSubmittalInDoneState,
    generalInfoDisabledTooltipMsg,
    getWorkflowFieldsDisabledTooltipMsg,
    getSubmitterDisabledTooltipMsg,
    assignDisabledTooltipMsg,
    contentClassNames,
    submittalParticipants,
    showReviewOption,
    isLoggedInUserMainInWorkflow,
    getAttachBtnDisabledMsg,
    showOverrideWorkflowBtn,
    loggedInUserId,
    loggedInUser,
    permission: {
      isLoggedInUserGcAdmin,
      isLoggedInUserGC,
      isLoggedInUserSC,
      isLoggedInUserArchitect,
      isLoggedInUserEngineer,
      isLoggedInUserOwner,
      isLoggedInUserWFGCUser,
      isCurrentAssigneeGCUser
    }
  };
};
